import React, { useState, useRef, useEffect } from "react";
import updateAOV from "../../utils/updateAOV";
import ReportCount from "../reports/ReportCount";
import updateConversionRate from "../../utils/updateConversionRate";
import AsyncModalRequestConfirmation from "../shared/AsyncModalRequestConfirmation";
import DuplicateAndRemixModal from "./DuplicateAndRemixModal";
import DuplicateToNewCampaignModal from "./DuplicateToNewCampaignModal";
import RemixSparkleIcon from "./ai/RemixSparkleIcon";
import { reportingGoals } from "../../src/reportingGoals";

const VariantListReport = (props) => {
  const variant = props.variant;
  const cta = props.cta;
  const website = props.website;
  const goals = reportingGoals(website);

  const duplicateVariantButton = useRef(null);

  const [range_start, setRangeStart] = useState(
    props.range_start ||
      new Date(Date.now() - 5 * 365 * 24 * 60 * 60 * 1000).toISOString()
  );
  const [range_end, setRangeEnd] = useState(
    props.range_end || new Date().toISOString()
  );
  const comparisonRangeStart = props.previous_range_start || null;
  const comparisonRangeEnd = props.previous_range_end || null;
  const comparisonLabel = props.comparison_label || "vs previous period";
  const comparisonExpected = Boolean(
    comparisonRangeStart && comparisonRangeEnd
  );
  const [isRemixModalOpen, setIsRemixModalOpen] = useState(false);
  const [
    isDuplicateToNewCampaignModalOpen,
    setIsDuplicateToNewCampaignModalOpen,
  ] = useState(false);
  const [initialRemixInstructions, setInitialRemixInstructions] =
    useState(null);

  const firstStepId = props.first_step_id || null;
  const stepScopedEvent = (eventType) =>
    eventType === "View" || eventType === "Complete";

  const buildMetricPayload = (
    reportType,
    eventType,
    metricRangeStart,
    metricRangeEnd
  ) => ({
    report_type: reportType,
    report: {
      website_id: website.id,
      event_type: eventType,
      contactable_type: "Cta",
      contactable_id: cta.id,
      variant_id: variant.id,
      range_start: metricRangeStart,
      range_end: metricRangeEnd,
      broadcast_id: null,
      step_id: stepScopedEvent(eventType) ? firstStepId : null,
      ...(eventType === "Complete" ? { element_id: null } : {}),
    },
  });

  const comparisonPayload = (reportType, eventType) =>
    comparisonExpected
      ? buildMetricPayload(
          reportType,
          eventType,
          comparisonRangeStart,
          comparisonRangeEnd
        )
      : null;

  const openDuplicateAndRemixModal = (instructions) => {
    setInitialRemixInstructions({
      remix_instructions: instructions.remix_instructions || "",
      variant_name: instructions.variant_name || "",
    });
    setIsRemixModalOpen(true);
  };

  useEffect(() => {
    const handleOpenDuplicateAndRemix = (e) => {
      if (e.detail && e.detail.variantId === variant.id) {
        openDuplicateAndRemixModal({
          remix_instructions: e.detail.remix_instructions,
          variant_name: e.detail.variant_name,
        });
      }
    };
    document.addEventListener(
      "openDuplicateAndRemix",
      handleOpenDuplicateAndRemix
    );
    return () => {
      document.removeEventListener(
        "openDuplicateAndRemix",
        handleOpenDuplicateAndRemix
      );
    };
  }, [variant.id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("open_duplicate_remix") !== "1" ||
      params.get("variant_id") !== String(variant.id)
    ) {
      return;
    }

    openDuplicateAndRemixModal({
      remix_instructions: params.get("remix_instructions") || "",
      variant_name: params.get("variant_name") || "",
    });

    const url = new URL(window.location.href);
    url.searchParams.delete("open_duplicate_remix");
    url.searchParams.delete("variant_id");
    url.searchParams.delete("remix_instructions");
    url.searchParams.delete("variant_name");
    window.history.replaceState({}, "", url.toString());
  }, [variant.id]);

  return (
    <React.Fragment>
      <div
        className="col-xs-1 variants-col variants-col-viewers"
        style={{ paddingTop: "25px" }}
      >
        <p
          id={`variant${variant.id}-viewers`}
          className="text-slate-500 variant-stat"
          data-name="viewers"
          data-toggle="tooltip"
          data-placement="bottom"
          data-container="body"
          title="Unique people who saw the first step of this variant"
          style={{ marginTop: "10px" }}
        >
          <ReportCount
            id={`cta-${cta.id}-variant-${variant.id}-views`}
            inline={true}
            unstyled={true}
            comparison_badge_below={true}
            hide_comparison_label={true}
            path={`/websites/${website.id}/events_reports`}
            payload={buildMetricPayload(
              "distinct_count",
              "View",
              range_start,
              range_end
            )}
            comparison_payload={comparisonPayload("distinct_count", "View")}
            comparison_label={comparisonLabel}
          />
        </p>
      </div>

      <div
        className="col-xs-1 variants-col variants-col-rate"
        style={{ paddingTop: "25px" }}
      >
        <p
          id={`variant${variant.id}-conversions`}
          className="text-slate-500 variant-stat"
          data-name="conversions"
          style={{ paddingTop: "10px" }}
        >
          <span
            data-toggle="tooltip"
            data-placement="bottom"
            data-container="body"
            title="Converted on first step button/form of this variant"
          >
            <ReportCount
              id={`cta-${cta.id}-variant-${variant.id}-conversions`}
              inline={true}
              unstyled={true}
              comparison_badge_below={true}
              hide_comparison_badge={true}
              hide_comparison_label={true}
              path={`/websites/${website.id}/events_reports`}
              payload={buildMetricPayload(
                "distinct_count",
                "Complete",
                range_start,
                range_end
              )}
              comparison_payload={comparisonPayload(
                "distinct_count",
                "Complete"
              )}
              comparison_label={comparisonLabel}
              callback={() => {
                updateConversionRate(
                  `#cta-${cta.id}-variant-${variant.id}-views`,
                  `#cta-${cta.id}-variant-${variant.id}-conversions`,
                  `#cta-${cta.id}-variant-${variant.id}-conversion-rate`,
                  {
                    comparison_expected: comparisonExpected,
                    comparison_label: comparisonLabel,
                    badge_below: true,
                    badgeContainer: `#cta-${cta.id}-variant-${variant.id}-conversion-rate-comparison`,
                    hide_comparison_label: true,
                  }
                );
              }}
            />{" "}
          </span>

          <span
            data-toggle="tooltip"
            data-placement="bottom"
            data-container="body"
            title="Percentage that converted on first step of this variant"
          >
            (
            <span id={`cta-${cta.id}-variant-${variant.id}-conversion-rate`}>
              0.00%
            </span>
            )
          </span>
          <div
            id={`cta-${cta.id}-variant-${variant.id}-conversion-rate-comparison`}
            style={{ marginTop: "4px", width: "fit-content" }}
          ></div>
        </p>
      </div>

      <div
        className="col-xs-1 variants-col variants-col-rate"
        style={{ paddingTop: "25px" }}
      >
        <p
          id={`variant${variant.id}-completions`}
          className="text-slate-500 variant-stat"
          data-name="completions"
          style={{ marginTop: "10px" }}
        >
          <span
            data-toggle="tooltip"
            data-placement="bottom"
            data-container="body"
            title="Tracked as having completed this campaign variant"
          >
            <ReportCount
              id={`cta-${cta.id}-variant-${variant.id}-completions`}
              inline={true}
              unstyled={true}
              comparison_badge_below={true}
              hide_comparison_badge={true}
              hide_comparison_label={true}
              path={`/websites/${website.id}/events_reports`}
              payload={buildMetricPayload(
                "distinct_count",
                "Completion",
                range_start,
                range_end
              )}
              comparison_payload={comparisonPayload(
                "distinct_count",
                "Completion"
              )}
              comparison_label={comparisonLabel}
              callback={() => {
                updateConversionRate(
                  `#cta-${cta.id}-variant-${variant.id}-views`,
                  `#cta-${cta.id}-variant-${variant.id}-completions`,
                  `#cta-${cta.id}-variant-${variant.id}-completion-rate`,
                  {
                    comparison_expected: comparisonExpected,
                    comparison_label: comparisonLabel,
                    badge_below: true,
                    badgeContainer: `#cta-${cta.id}-variant-${variant.id}-completion-rate-comparison`,
                    hide_comparison_label: true,
                  }
                );
              }}
            />{" "}
          </span>

          <span
            data-toggle="tooltip"
            data-placement="bottom"
            data-container="body"
            title="Percentage that completed this campaign variant"
          >
            (
            <span id={`cta-${cta.id}-variant-${variant.id}-completion-rate`}>
              0.00%
            </span>
            )
          </span>
          <div
            id={`cta-${cta.id}-variant-${variant.id}-completion-rate-comparison`}
            style={{ marginTop: "4px", width: "fit-content" }}
          ></div>
        </p>
      </div>

      {goals.checkouts && (
        <div
          className="col-xs-1 variants-col variants-col-rate"
          style={{ paddingTop: "25px" }}
        >
          <p
            id={`variant${variant.id}-orders`}
            className="text-slate-500 variant-stat"
            data-name="orders"
            style={{ marginTop: "10px" }}
          >
            <span
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title="Orders within the configured attribution window after converting with this campaign"
            >
              <ReportCount
                id={`cta-${cta.id}-variant-${variant.id}-orders`}
                inline={true}
                unstyled={true}
                comparison_badge_below={true}
                hide_comparison_badge={true}
                hide_comparison_label={true}
                path={`/websites/${website.id}/events_reports`}
                payload={buildMetricPayload(
                  "count",
                  "Order",
                  range_start,
                  range_end
                )}
                comparison_payload={comparisonPayload("count", "Order")}
                comparison_label={comparisonLabel}
                callback={() => {
                  updateConversionRate(
                    `#cta-${cta.id}-variant-${variant.id}-views`,
                    `#cta-${cta.id}-variant-${variant.id}-orders`,
                    `#cta-${cta.id}-variant-${variant.id}-orders-rate`,
                    {
                      comparison_expected: comparisonExpected,
                      comparison_label: comparisonLabel,
                      badge_below: true,
                      badgeContainer: `#cta-${cta.id}-variant-${variant.id}-orders-rate-comparison`,
                      hide_comparison_label: true,
                    }
                  );

                  updateAOV(
                    `#cta-${cta.id}-variant-${variant.id}-orders`,
                    `#cta-${cta.id}-variant-${variant.id}-revenue`,
                    `#cta-${cta.id}-variant-${variant.id}-aov`,
                    {
                      comparison_expected: comparisonExpected,
                      comparison_label: comparisonLabel,
                      badgeContainer: `#cta-${cta.id}-variant-${variant.id}-aov-comparison`,
                      hide_comparison_label: true,
                    }
                  );
                }}
              />{" "}
            </span>

            <span
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title="Percentage of orders that converted and then checked out within the configured attribution window"
            >
              (
              <span id={`cta-${cta.id}-variant-${variant.id}-orders-rate`}>
                0.00%
              </span>
              )
            </span>
            <div
              id={`cta-${cta.id}-variant-${variant.id}-orders-rate-comparison`}
              style={{ marginTop: "4px", width: "fit-content" }}
            ></div>
          </p>
        </div>
      )}

      {goals.checkouts && (
        <div
          className="col-xs-1 variants-col variants-col-metric"
          style={{ paddingTop: "25px" }}
        >
          <p
            id={`variant${variant.id}-revenue`}
            className="text-slate-500 variant-stat"
            data-name="revenue"
            style={{ marginTop: "10px" }}
          >
            <span
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title="Total revenue of orders that occured within the configured attribution window of converting"
            >
              <ReportCount
                id={`cta-${cta.id}-variant-${variant.id}-revenue`}
                inline={true}
                unstyled={true}
                comparison_badge_below={true}
                hide_comparison_label={true}
                currency={props.currency}
                path={`/websites/${website.id}/events_reports`}
                payload={buildMetricPayload(
                  "revenue",
                  "Order",
                  range_start,
                  range_end
                )}
                comparison_payload={comparisonPayload("revenue", "Order")}
                comparison_label={comparisonLabel}
              />
            </span>
          </p>
        </div>
      )}

      {goals.checkouts && (
        <div
          className="col-xs-1 variants-col variants-col-metric"
          style={{ paddingTop: "25px" }}
        >
          <p
            className="text-slate-500 variant-stat"
            data-name="aov"
            style={{ marginTop: "10px" }}
          >
            {props.currency}
            <span
              id={`cta-${cta.id}-variant-${variant.id}-aov`}
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title="Average revenue of orders that occured within the configured attribution window of converting"
            >
              0.00
            </span>
            <div
              id={`cta-${cta.id}-variant-${variant.id}-aov-comparison`}
              style={{ marginTop: "4px", width: "fit-content" }}
            ></div>
          </p>
        </div>
      )}

      {goals.bookings && (
        <div
          className="col-xs-1 variants-col variants-col-metric"
          style={{ paddingTop: "25px" }}
        >
          <p
            id={`variant${variant.id}-bookings`}
            className="text-slate-500 variant-stat"
            data-name="bookings"
            style={{ marginTop: "10px" }}
          >
            <span
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title="Unique visitors who booked a meeting through a scheduler element"
            >
              <ReportCount
                id={`cta-${cta.id}-variant-${variant.id}-bookings`}
                inline={true}
                unstyled={true}
                comparison_badge_below={true}
                hide_comparison_label={true}
                path={`/websites/${website.id}/events_reports`}
                payload={buildMetricPayload(
                  "distinct_count",
                  "Booking",
                  range_start,
                  range_end
                )}
                comparison_payload={comparisonPayload(
                  "distinct_count",
                  "Booking"
                )}
                comparison_label={comparisonLabel}
              />
            </span>
          </p>
        </div>
      )}

      {goals.custom && (
        <div
          className="col-xs-1 variants-col variants-col-metric"
          style={{ paddingTop: "25px" }}
        >
          <p
            id={`variant${variant.id}-custom-goals`}
            className="text-slate-500 variant-stat"
            data-name="custom_goals"
            style={{ marginTop: "10px" }}
          >
            <span
              data-toggle="tooltip"
              data-placement="bottom"
              data-container="body"
              title={`Unique visitors who completed ${goals.customGoalLabel}`}
            >
              <ReportCount
                id={`cta-${cta.id}-variant-${variant.id}-custom-goals`}
                inline={true}
                unstyled={true}
                comparison_badge_below={true}
                hide_comparison_label={true}
                path={`/websites/${website.id}/events_reports`}
                payload={buildMetricPayload(
                  "distinct_count",
                  "Custom Goal",
                  range_start,
                  range_end
                )}
                comparison_payload={comparisonPayload(
                  "distinct_count",
                  "Custom Goal"
                )}
                comparison_label={comparisonLabel}
              />
            </span>
          </p>
        </div>
      )}

      <div
        className="variants-col variants-col-actions sticky-options-col"
        style={{ paddingTop: "25px" }}
      >
        <div className="btn-group">
          <button
            type="button"
            className="bg-white border-solid border-[1px] py-3 px-5 mr-4 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Options <span className="caret"></span>
          </button>
          <ul className="!rounded-lg !border-none !shadow-lg dropdown-menu dropdown-menu-right">
            <li>
              <a href={props.builder_path} data-turbolinks="false">
                Builder
              </a>
            </li>
            <li>
              <a href={props.preview_path}>Preview</a>
            </li>
            <li>
              <a
                className="variant-create cursor-pointer"
                data-turbolinks="false"
                ref={duplicateVariantButton}
                onClick={(e) => {
                  if (
                    website.premium !== true ||
                    ["2026_personal", "2024_pages"].includes(website.plan_type)
                  ) {
                    e.preventDefault();

                    subscriptions.upgrade_modal(
                      e,
                      false,
                      "create and manage variants for A/B split-tests",
                      "pro",
                      "Triggered feature CTA – A/B Split Testing"
                    );
                  }
                }}
              >
                Duplicate
              </a>
            </li>

            <li>
              <a
                className="cursor-pointer flex items-center gap-2"
                style={{
                  color: "#003FFF",
                  display:
                    website.data && website.data["ai-enabled"] !== "false"
                      ? "flex"
                      : "none",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setIsRemixModalOpen(true);
                }}
              >
                Duplicate & remix
                <RemixSparkleIcon width={12} height={13} color="#003FFF" />
              </a>
            </li>

            <li>
              <a
                className="new-cta cursor-pointer"
                data-turbolinks="false"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDuplicateToNewCampaignModalOpen(true);
                }}
              >
                Copy to new campaign
              </a>
            </li>

            {props.account_role_type != "client" && (
              <React.Fragment>
                <li>
                  <a
                    className=" template-create"
                    target="_blank"
                    href={props.save_template_path}
                  >
                    Save as template
                  </a>
                </li>
                {["tech", "marketing"].includes(
                  props.current_user?.admin_type
                ) && (
                  <li>
                    <a
                      className=" template-create"
                      target="_blank"
                      href={props.save_official_template_path}
                    >
                      Save as official template
                    </a>
                  </li>
                )}
              </React.Fragment>
            )}

            <li>
              <a href={props.contacts_path}>Contacts</a>
            </li>

            <li>
              <a
                data-type="variant-delete"
                data-id={variant.id}
                id={`variant${variant.id}-delete`}
                style={{
                  display: variant.percentage != 0 ? "none" : "",
                }}
                href="#"
              >
                Delete
              </a>
            </li>
          </ul>
        </div>
      </div>

      {website.premium == true &&
        ["2026_personal", "2024_pages"].includes(website.plan_type) ==
          false && (
          <AsyncModalRequestConfirmation
            triggerRef={duplicateVariantButton}
            loadingMessage="Duplicating..."
            endpoint={props.duplicate_path}
            readyCallback={(data) => {
              const variant = data.object;
              return `/websites/${website.id}/cta/${cta.id}/variants/${variant.id}/edit`;
            }}
            params={{
              id: variant.id,
            }}
          />
        )}

      <DuplicateToNewCampaignModal
        isOpen={isDuplicateToNewCampaignModalOpen}
        onClose={() => setIsDuplicateToNewCampaignModalOpen(false)}
        cta={cta}
        variant={variant}
        endpoint={props.duplicate_to_new_campaign_path}
        onSuccess={(data) => {
          const newCta = data.object;
          return `/websites/${newCta.website_id}/cta/${newCta.id}`;
        }}
      />

      <DuplicateAndRemixModal
        isOpen={isRemixModalOpen}
        onClose={() => {
          setIsRemixModalOpen(false);
          setInitialRemixInstructions(null);
        }}
        variant={variant}
        website={website}
        cta={cta}
        duplicateAndRemixPath={props.duplicate_and_remix_path}
        currentUser={props.current_user}
        initialRemixInstructions={initialRemixInstructions}
        onSuccess={(data) => {
          const newVariant = data.object;
          window.location.href = `/websites/${website.id}/cta/${cta.id}/variants/${newVariant.id}/edit`;
        }}
      />
    </React.Fragment>
  );
};

export default VariantListReport;
